import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useTheme } from '../../../contexts/ThemeContext';
import { useDashboardUi } from '../../../config/dashboardUiTheme';
import { cn } from '../../../lib/utils';
import {
  categoryBadgeClassName,
  setupCardAccentBarClassName,
  setupGroupHeaderClassName,
  setupLinkedDeptBadgeClassName,
  setupOrderChipClassName,
  setupPageHeroClassName,
  setupSelectedCardClassName,
} from '../../../config/tableBadgeStyles';
import {
  Card,
  CardHeader,
  CardTitle,
  CardDescription,
  CardContent,
  Button,
  Input,
  Textarea,
  Label,
  Select,
  SelectItem,
  Modal,
  ModalHeader,
  ModalTitle,
  ModalDescription,
  ModalContent,
  ModalFooter,
} from '../../../components/ui';
import { PencilIcon, TrashIcon, FolderPlusIcon } from '../../../components/ui/Icons';
import {
  fetchTicketingDepartments,
  fetchTicketingCategories,
  createTicketingDepartment,
  updateTicketingDepartment,
  createTicketingCategory,
  updateTicketingCategory,
  deleteTicketingDepartment,
  deleteTicketingCategory,
  fetchUserDepartments,
} from '../ticketingApi';
import { DEFAULT_TICKETING_FORM_KEY, TICKETING_DATA_CHANGED_EVENT } from '../ticketingStorage';
import { TICKETING_FORM_OPTIONS } from '../ticketingFormKeys';

const emptyDepartmentForm = () => ({
  name: '',
  description: '',
  isActive: true,
  recordOrder: '',
  userDepartmentId: '',
});

const emptyCategoryForm = () => ({
  name: '',
  categoryGroup: '',
  description: '',
  isActive: true,
  recordOrder: '',
  formKey: DEFAULT_TICKETING_FORM_KEY,
});

const TicketingHrPage = () => {
  const { theme } = useTheme();
  const dash = useDashboardUi(theme);

  const normalizeOrder = (v) => {
    if (v == null || v === '') return Number.POSITIVE_INFINITY;
    const n = Number(v);
    return Number.isFinite(n) ? n : Number.POSITIVE_INFINITY;
  };

  const compareByOrderThenName = (a, b) => {
    const orderDiff = normalizeOrder(a.record_order) - normalizeOrder(b.record_order);
    if (orderDiff !== 0) return orderDiff;
    return String(a.name || '').localeCompare(String(b.name || ''), undefined, {
      sensitivity: 'base',
    });
  };

  const [departments, setDepartments] = useState([]);
  const [categories, setCategories] = useState([]);
  const [userDepartments, setUserDepartments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedDepartmentId, setSelectedDepartmentId] = useState(null);
  const [copySourceDepartmentId, setCopySourceDepartmentId] = useState('');
  const [copyingCategories, setCopyingCategories] = useState(false);

  const [departmentModalOpen, setDepartmentModalOpen] = useState(false);
  const [editingDepartmentId, setEditingDepartmentId] = useState(null);
  const [departmentForm, setDepartmentForm] = useState(emptyDepartmentForm);

  const [categoryModalOpen, setCategoryModalOpen] = useState(false);
  const [editingCategoryId, setEditingCategoryId] = useState(null);
  const [categoryForm, setCategoryForm] = useState(emptyCategoryForm);

  const sortedDepartments = useMemo(
    () => [...departments].sort(compareByOrderThenName),
    [departments]
  );

  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      const [depts, cats] = await Promise.all([
        fetchTicketingDepartments(),
        fetchTicketingCategories(),
      ]);
      const userDeptRows = await fetchUserDepartments();
      setDepartments(depts);
      setCategories(cats);
      setUserDepartments(Array.isArray(userDeptRows) ? userDeptRows : []);
    } catch (err) {
      console.error(err);
      alert(err?.message || 'Failed to load ticketing data. Are you logged in?');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadData();
  }, [loadData]);

  useEffect(() => {
    const onEvt = () => loadData();
    window.addEventListener(TICKETING_DATA_CHANGED_EVENT, onEvt);
    return () => window.removeEventListener(TICKETING_DATA_CHANGED_EVENT, onEvt);
  }, [loadData]);

  const selectedDepartment = useMemo(
    () => departments.find((d) => d.id === selectedDepartmentId) || null,
    [departments, selectedDepartmentId]
  );

  const selectedCategories = useMemo(
    () =>
      categories
        .filter((c) => c.departmentId === selectedDepartmentId)
        .sort((a, b) => {
          const groupA = String(a.categoryGroup || '').trim().toLowerCase();
          const groupB = String(b.categoryGroup || '').trim().toLowerCase();
          const groupDiff = groupA.localeCompare(groupB, undefined, { sensitivity: 'base' });
          if (groupDiff !== 0) return groupDiff;
          return compareByOrderThenName(a, b);
        }),
    [categories, selectedDepartmentId]
  );
  const groupedSelectedCategories = useMemo(() => {
    const buckets = new Map();
    selectedCategories.forEach((cat) => {
      const key = String(cat.categoryGroup || '').trim() || 'Ungrouped';
      if (!buckets.has(key)) buckets.set(key, []);
      buckets.get(key).push(cat);
    });
    return Array.from(buckets.entries()).map(([groupName, items]) => ({
      groupName,
      items,
    }));
  }, [selectedCategories]);

  const parseRecordOrder = (raw) => {
    const s = String(raw ?? '').trim();
    if (s === '') return null;
    const n = parseInt(s, 10);
    if (Number.isNaN(n)) {
      alert('Record order must be a whole number or empty.');
      return undefined;
    }
    return n;
  };

  const closeDepartmentModal = () => {
    setDepartmentModalOpen(false);
    setEditingDepartmentId(null);
    setDepartmentForm(emptyDepartmentForm());
  };

  const openAddDepartmentModal = () => {
    setEditingDepartmentId(null);
    setDepartmentForm(emptyDepartmentForm());
    setDepartmentModalOpen(true);
  };

  const openEditDepartmentModal = (department, e) => {
    e?.stopPropagation?.();
    setEditingDepartmentId(department.id);
    setSelectedDepartmentId(department.id);
    setDepartmentForm({
      name: department.name || '',
      description: department.description || '',
      recordOrder:
        department.record_order != null && department.record_order !== ''
          ? String(department.record_order)
          : '',
      userDepartmentId:
        department.userDepartmentId != null ? String(department.userDepartmentId) : '',
    });
    setDepartmentModalOpen(true);
  };

  const handleDepartmentSubmit = async (e) => {
    e.preventDefault();
    const name = departmentForm.name.trim();
    if (!name) return;
    if (!departmentForm.userDepartmentId) {
      alert('Linked user department is required.');
      return;
    }

    const record_order = parseRecordOrder(departmentForm.recordOrder);
    if (record_order === undefined) return;

    const description = departmentForm.description.trim();

    try {
      if (editingDepartmentId != null) {
        const exists = departments.some(
          (d) =>
            d.id !== editingDepartmentId &&
            d.name.toLowerCase() === name.toLowerCase()
        );
        if (exists) {
          alert('Another department already uses this name.');
          return;
        }
        await updateTicketingDepartment(editingDepartmentId, {
          name,
          description: description || null,
          isActive: true,
          record_order,
          userDepartmentId: Number(departmentForm.userDepartmentId),
        });
      } else {
        const exists = departments.some((d) => d.name.toLowerCase() === name.toLowerCase());
        if (exists) {
          alert('Department already exists.');
          return;
        }
        const created = await createTicketingDepartment({
          name,
          description: description || null,
          isActive: true,
          record_order,
          userDepartmentId: Number(departmentForm.userDepartmentId),
        });
        setSelectedDepartmentId(created.id);
      }
      await loadData();
      closeDepartmentModal();
    } catch (err) {
      alert(err?.message || 'Save failed');
    }
  };

  const closeCategoryModal = () => {
    setCategoryModalOpen(false);
    setEditingCategoryId(null);
    setCategoryForm(emptyCategoryForm());
  };

  const openAddCategoryModal = () => {
    if (!selectedDepartmentId) return;
    setEditingCategoryId(null);
    setCategoryForm(emptyCategoryForm());
    setCategoryModalOpen(true);
  };

  const openEditCategoryModal = (category) => {
    setEditingCategoryId(category.id);
    setCategoryForm({
      name: category.name || '',
      categoryGroup: category.categoryGroup || '',
      description: category.description || '',
      recordOrder:
        category.record_order != null && category.record_order !== ''
          ? String(category.record_order)
          : '',
      formKey: category.formKey || DEFAULT_TICKETING_FORM_KEY,
    });
    setCategoryModalOpen(true);
  };

  const handleCategorySubmit = async (e) => {
    e.preventDefault();
    if (!selectedDepartmentId) return;

    const name = categoryForm.name.trim();
    if (!name) return;

    const record_order = parseRecordOrder(categoryForm.recordOrder);
    if (record_order === undefined) return;

    const description = categoryForm.description.trim();
    const categoryGroup = categoryForm.categoryGroup.trim();
    const formKey = categoryForm.formKey || DEFAULT_TICKETING_FORM_KEY;

    try {
      if (editingCategoryId != null) {
        const nameExists = categories.some(
          (c) =>
            c.departmentId === selectedDepartmentId &&
            c.id !== editingCategoryId &&
            c.name.toLowerCase() === name.toLowerCase()
        );
        if (nameExists) {
          alert('Another category in this department already uses this name.');
          return;
        }
        await updateTicketingCategory(editingCategoryId, {
          name,
          categoryGroup: categoryGroup || null,
          description: description || null,
          isActive: true,
          record_order,
          formKey,
          departmentId: selectedDepartmentId,
        });
      } else {
        const nameExists = categories.some(
          (c) =>
            c.departmentId === selectedDepartmentId &&
            c.name.toLowerCase() === name.toLowerCase()
        );
        if (nameExists) {
          alert('Category already exists in this department.');
          return;
        }
        await createTicketingCategory({
          departmentId: selectedDepartmentId,
          name,
          categoryGroup: categoryGroup || null,
          description: description || null,
          isActive: true,
          record_order,
          formKey,
        });
      }
      await loadData();
      closeCategoryModal();
    } catch (err) {
      alert(err?.message || 'Save failed');
    }
  };

  const handleDeleteDepartment = async (department, e) => {
    e?.stopPropagation?.();
    const ok = window.confirm(
      `Delete department "${department.name}"? This works only when it has no ticket requests.`
    );
    if (!ok) return;
    try {
      await deleteTicketingDepartment(department.id);
      if (selectedDepartmentId === department.id) {
        setSelectedDepartmentId(null);
      }
      await loadData();
    } catch (err) {
      alert(err?.message || 'Delete failed');
    }
  };

  const handleDeleteCategory = async (category) => {
    const ok = window.confirm(
      `Delete category "${category.name}"? This works only when it has no ticket requests.`
    );
    if (!ok) return;
    try {
      await deleteTicketingCategory(category.id);
      await loadData();
    } catch (err) {
      alert(err?.message || 'Delete failed');
    }
  };

  const handleCopyCategoriesFromDepartment = async () => {
    if (!selectedDepartmentId) {
      alert('Select a target department first.');
      return;
    }
    const sourceDepartmentId = Number(copySourceDepartmentId);
    if (!sourceDepartmentId || Number.isNaN(sourceDepartmentId)) {
      alert('Select a source department.');
      return;
    }
    if (sourceDepartmentId === selectedDepartmentId) {
      alert('Source and target department must be different.');
      return;
    }

    const sourceDepartment = departments.find((d) => d.id === sourceDepartmentId);
    const targetDepartment = departments.find((d) => d.id === selectedDepartmentId);
    const sourceCategories = categories.filter((c) => c.departmentId === sourceDepartmentId);

    if (sourceCategories.length === 0) {
      alert(`No categories found in ${sourceDepartment?.name || 'selected department'}.`);
      return;
    }

    const ok = window.confirm(
      `Copy ${sourceCategories.length} categories from "${sourceDepartment?.name || sourceDepartmentId}" to "${targetDepartment?.name || selectedDepartmentId}"? Existing categories with same name are skipped.`
    );
    if (!ok) return;

    setCopyingCategories(true);
    try {
      const existingNames = new Set(
        categories
          .filter((c) => c.departmentId === selectedDepartmentId)
          .map((c) => c.name.trim().toLowerCase())
      );

      let createdCount = 0;
      let skippedCount = 0;

      for (const source of sourceCategories) {
        const normalized = String(source.name || '').trim().toLowerCase();
        if (!normalized || existingNames.has(normalized)) {
          skippedCount += 1;
          continue;
        }
        await createTicketingCategory({
          departmentId: selectedDepartmentId,
          name: source.name,
          categoryGroup: source.categoryGroup || null,
          description: source.description || null,
          isActive: source.isActive !== false,
          record_order: source.record_order ?? null,
          formKey: source.formKey || DEFAULT_TICKETING_FORM_KEY,
        });
        existingNames.add(normalized);
        createdCount += 1;
      }

      await loadData();
      alert(`Copy completed. Created: ${createdCount}. Skipped: ${skippedCount}.`);
    } catch (err) {
      alert(err?.message || 'Failed to copy categories');
    } finally {
      setCopyingCategories(false);
    }
  };

  return (
    <div className={cn('min-h-full p-3 md:p-4 space-y-3', dash.pageBg())}>
      <div className={setupPageHeroClassName()}>
        <h1 className={cn('text-lg md:text-xl font-semibold', theme.text.primary)}>Ticketing Setup</h1>
        <p className={cn('text-sm mt-1', theme.text.secondary)}>
          Manage departments and organize request categories with forms and groups.
        </p>
      </div>
      {loading ? (
        <p className={cn('text-sm mb-4', theme.text.muted)}>Loading ticketing data…</p>
      ) : null}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <Card className={cn(dash.widgetShell('overflow-hidden p-0'))}>
          <div className={setupCardAccentBarClassName()} />
          <CardHeader className="flex flex-row flex-wrap items-start justify-between gap-3 space-y-0">
            <div>
              <CardTitle>Ticketing Departments</CardTitle>
              <CardDescription>Select a department, then manage categories on the right</CardDescription>
            </div>
            <Button type="button" variant="primary" size="sm" onClick={openAddDepartmentModal}>
              Add department
            </Button>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="space-y-2 max-h-[520px] overflow-y-auto pr-1">
              {departments.length === 0 ? (
                <p className={cn('text-sm', theme.text.muted)}>No departments yet. Use Add department.</p>
              ) : (
                sortedDepartments.map((department) => (
                  <div
                    key={department.id}
                    role="button"
                    tabIndex={0}
                    onClick={() => setSelectedDepartmentId(department.id)}
                    onKeyDown={(ev) => {
                      if (ev.key === 'Enter' || ev.key === ' ') {
                        ev.preventDefault();
                        setSelectedDepartmentId(department.id);
                      }
                    }}
                    className={cn(
                      'w-full text-left rounded-xl border p-4 transition cursor-pointer',
                      setupSelectedCardClassName(selectedDepartmentId === department.id),
                    )}
                  >
                    <div className="flex items-start justify-between gap-3">
                      <div className="min-w-0 flex-1">
                        <div className={cn('font-semibold', theme.text.primary)}>
                          {department.name}
                        </div>
                        {department.description ? (
                          <div className={cn('text-xs mt-1', theme.text.muted)}>{department.description}</div>
                        ) : null}
                        <div className="flex flex-wrap gap-2 mt-2">
                          {department.record_order != null && department.record_order !== '' ? (
                            <span
                              className={cn(
                                'inline-flex items-center text-[11px] font-semibold tabular-nums uppercase tracking-wide',
                                setupOrderChipClassName(theme),
                              )}
                            >
                              #{department.record_order}
                            </span>
                          ) : null}
                        {department.userDepartment?.name ? (
                          <span
                            className={cn(
                              'inline-flex items-center mt-2 text-[11px] font-medium',
                              setupLinkedDeptBadgeClassName(theme),
                            )}
                          >
                            Linked user dept: {department.userDepartment.name}
                          </span>
                        ) : null}
                        </div>
                      </div>
                      <div className="flex items-center gap-1">
                        <Button
                          type="button"
                          variant="ghost"
                          size="icon"
                          onClick={(ev) => openEditDepartmentModal(department, ev)}
                          title="Edit department"
                          aria-label={`Edit department ${department.name}`}
                        >
                          <PencilIcon className="h-4 w-4" />
                        </Button>
                        <Button
                          type="button"
                          variant="ghost"
                          size="icon"
                          className="text-red-600 hover:bg-red-50 dark:text-red-400 dark:hover:bg-red-950/30"
                          onClick={(ev) => handleDeleteDepartment(department, ev)}
                          title="Delete department"
                          aria-label={`Delete department ${department.name}`}
                        >
                          <TrashIcon className="h-4 w-4" />
                        </Button>
                      </div>
                    </div>
                  </div>
                ))
              )}
            </div>
          </CardContent>
        </Card>

        <Card className={cn(dash.widgetShell('overflow-hidden p-0'))}>
          <div className={setupCardAccentBarClassName()} />
          <CardHeader className="space-y-3">
            <div className="flex flex-row flex-wrap items-start justify-between gap-3">
              <div className="flex items-start gap-3 min-w-0">
                <div
                  className={cn(
                    'h-10 w-10 rounded-xl flex items-center justify-center shrink-0',
                    dash.iconBg('info'),
                    dash.text('info'),
                  )}
                >
                  <FolderPlusIcon className="h-5 w-5" />
                </div>
                <div className="min-w-0">
                  <CardTitle className="leading-tight">Ticketing Categories</CardTitle>
                  <CardDescription className="mt-1">
                    {selectedDepartment
                      ? `Manage request types for ${selectedDepartment.name}`
                      : 'Select a department from the left'}
                  </CardDescription>
                </div>
              </div>
              <div className="flex items-center gap-2 flex-wrap">
                <span className={dash.badge('neutral')}>
                  {selectedDepartment ? selectedCategories.length : 0} categories
                </span>
                {selectedDepartment ? (
                  <span className={cn(dash.badge('info'), 'max-w-[220px] truncate')}>
                    {selectedDepartment.name}
                  </span>
                ) : null}
              </div>
            </div>

            <div className="flex items-center gap-2 flex-wrap justify-end pt-1">
              <div className="min-w-[220px]">
                <Select
                  value={copySourceDepartmentId}
                  onChange={(e) => setCopySourceDepartmentId(e.target.value)}
                  disabled={!selectedDepartment || copyingCategories}
                  className="w-full"
                >
                  <SelectItem value="">Copy from department…</SelectItem>
                  {sortedDepartments
                    .filter((d) => d.id !== selectedDepartmentId)
                    .map((d) => (
                      <SelectItem key={d.id} value={String(d.id)}>
                        {d.name}
                      </SelectItem>
                    ))}
                </Select>
              </div>
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={handleCopyCategoriesFromDepartment}
                disabled={!selectedDepartment || !copySourceDepartmentId || copyingCategories}
              >
                {copyingCategories ? 'Copying…' : 'Copy'}
              </Button>
              <Button
                type="button"
                variant="primary"
                size="sm"
                onClick={openAddCategoryModal}
                disabled={!selectedDepartment}
              >
                Add category
              </Button>
            </div>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="space-y-2 max-h-[520px] overflow-y-auto pr-1">
              {!selectedDepartment ? (
                <p className={cn('text-sm', theme.text.muted)}>Select a department first.</p>
              ) : selectedCategories.length === 0 ? (
                <p className={cn('text-sm', theme.text.muted)}>No categories yet. Use Add category.</p>
              ) : (
                groupedSelectedCategories.map((groupBlock) => (
                  <div key={groupBlock.groupName} className="space-y-2">
                    <div className={setupGroupHeaderClassName()}>
                      {groupBlock.groupName}
                    </div>
                    {groupBlock.items.map((category) => {
                      const fk = category.formKey || DEFAULT_TICKETING_FORM_KEY;
                      const formTemplateLabel =
                        TICKETING_FORM_OPTIONS.find((o) => o.value === fk)?.label || fk;
                      return (
                        <div
                          key={category.id}
                          className={cn(
                            'flex items-start justify-between gap-3 transition rounded-xl',
                            dash.listItem(),
                          )}
                        >
                          <div className="min-w-0 flex-1">
                            <div className="flex items-center gap-2 flex-wrap">
                              <span className={cn('font-semibold', theme.text.primary)}>
                                {category.name}
                              </span>
                              <span
                                className={cn(
                                  'text-xs font-normal max-w-[220px] truncate',
                                  categoryBadgeClassName(theme),
                                )}
                              >
                                Form: {formTemplateLabel}
                              </span>
                            </div>
                            {category.record_order != null && category.record_order !== '' ? (
                              <div className="mt-2">
                                <span
                                  className={cn(
                                    'inline-flex items-center text-[11px] font-semibold tabular-nums uppercase tracking-wide',
                                    setupOrderChipClassName(theme),
                                  )}
                                >
                                  #{category.record_order}
                                </span>
                              </div>
                            ) : null}
                            {category.description ? (
                              <div className={cn('text-xs mt-1', theme.text.muted)}>{category.description}</div>
                            ) : null}
                          </div>
                          <div className="flex items-center gap-1">
                            <Button
                              type="button"
                              variant="ghost"
                              size="icon"
                              onClick={() => openEditCategoryModal(category)}
                              title="Edit category"
                              aria-label={`Edit category ${category.name}`}
                            >
                              <PencilIcon className="h-4 w-4" />
                            </Button>
                            <Button
                              type="button"
                              variant="ghost"
                              size="icon"
                              className="text-red-600 hover:bg-red-50 dark:text-red-400 dark:hover:bg-red-950/30"
                              onClick={() => handleDeleteCategory(category)}
                              title="Delete category"
                              aria-label={`Delete category ${category.name}`}
                            >
                              <TrashIcon className="h-4 w-4" />
                            </Button>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                ))
              )}
            </div>
          </CardContent>
        </Card>
      </div>

      <Modal isOpen={departmentModalOpen} onClose={closeDepartmentModal} size="sm">
        <ModalHeader onClose={closeDepartmentModal}>
          <ModalTitle>{editingDepartmentId != null ? 'Edit department' : 'Add department'}</ModalTitle>
          <ModalDescription>
            {editingDepartmentId != null
              ? 'Update name, description, order, and active status.'
              : 'Create a new ticketing department.'}
          </ModalDescription>
        </ModalHeader>
        <form onSubmit={handleDepartmentSubmit}>
          <ModalContent className="space-y-3">
            <Input
              value={departmentForm.name}
              onChange={(e) => setDepartmentForm((p) => ({ ...p, name: e.target.value }))}
              placeholder="Department name (e.g. Human Resources)"
            />
            <Textarea
              value={departmentForm.description}
              onChange={(e) =>
                setDepartmentForm((p) => ({ ...p, description: e.target.value }))
              }
              rows={3}
              placeholder="Optional description"
            />
            <Input
              type="number"
              value={departmentForm.recordOrder}
              onChange={(e) =>
                setDepartmentForm((p) => ({ ...p, recordOrder: e.target.value }))
              }
              placeholder="Record order (optional)"
            />
            <div>
              <Label htmlFor="ticketing-user-department" className="mb-1.5 block">
                Linked user department *
              </Label>
              <Select
                id="ticketing-user-department"
                name="userDepartmentId"
                value={departmentForm.userDepartmentId}
                onChange={(e) =>
                  setDepartmentForm((p) => ({
                    ...p,
                    userDepartmentId: e.target.value,
                  }))
                }
                showClearButton={false}
                className="w-full"
              >
                {userDepartments.map((d) => (
                  <SelectItem key={d.id} value={String(d.id)}>
                    {d.name}
                  </SelectItem>
                ))}
              </Select>
            </div>
          </ModalContent>
          <ModalFooter className="flex flex-wrap justify-end gap-2">
            <Button type="button" variant="outline" onClick={closeDepartmentModal}>
              Cancel
            </Button>
            <Button type="submit" variant="primary">
              {editingDepartmentId != null ? 'Save changes' : 'Create department'}
            </Button>
          </ModalFooter>
        </form>
      </Modal>

      <Modal isOpen={categoryModalOpen} onClose={closeCategoryModal} size="sm">
        <ModalHeader onClose={closeCategoryModal}>
          <ModalTitle>{editingCategoryId != null ? 'Edit category' : 'Add category'}</ModalTitle>
          <ModalDescription>
            {selectedDepartment
              ? `Department: ${selectedDepartment.name}`
              : 'Select a department first.'}
          </ModalDescription>
        </ModalHeader>
        <form onSubmit={handleCategorySubmit}>
          <ModalContent className="space-y-3">
            <Input
              value={categoryForm.name}
              onChange={(e) => setCategoryForm((p) => ({ ...p, name: e.target.value }))}
              placeholder="Category name (e.g. New Employee)"
              disabled={!selectedDepartment}
            />
            <Input
              value={categoryForm.categoryGroup}
              onChange={(e) =>
                setCategoryForm((p) => ({ ...p, categoryGroup: e.target.value }))
              }
              placeholder="Group (optional, e.g. Onboarding)"
              disabled={!selectedDepartment}
            />
            <Textarea
              value={categoryForm.description}
              onChange={(e) => setCategoryForm((p) => ({ ...p, description: e.target.value }))}
              rows={3}
              placeholder="Optional description"
              disabled={!selectedDepartment}
            />
            <Input
              type="number"
              value={categoryForm.recordOrder}
              onChange={(e) =>
                setCategoryForm((p) => ({ ...p, recordOrder: e.target.value }))
              }
              placeholder="Record order (optional)"
              disabled={!selectedDepartment}
            />
            <div>
              <Label htmlFor="category-form-key" className="mb-1.5 block">
                Request form template
              </Label>
              <Select
                id="category-form-key"
                name="formKey"
                value={categoryForm.formKey || DEFAULT_TICKETING_FORM_KEY}
                onChange={(e) =>
                  setCategoryForm((p) => ({ ...p, formKey: e.target.value }))
                }
                disabled={!selectedDepartment}
                showClearButton={false}
                className="w-full"
              >
                {TICKETING_FORM_OPTIONS.map((opt) => (
                  <SelectItem key={opt.value} value={opt.value}>
                    {opt.label}
                  </SelectItem>
                ))}
              </Select>
              <p className={cn('text-xs mt-1', theme.text.muted)}>
                Controls which fields users see when they open this request type.
              </p>
            </div>
          </ModalContent>
          <ModalFooter className="flex flex-wrap justify-end gap-2">
            <Button type="button" variant="outline" onClick={closeCategoryModal}>
              Cancel
            </Button>
            <Button type="submit" variant="primary" disabled={!selectedDepartment}>
              {editingCategoryId != null ? 'Save changes' : 'Create category'}
            </Button>
          </ModalFooter>
        </form>
      </Modal>
    </div>
  );
};

export default TicketingHrPage;
